"use client";

import { LazyPopupOptInNudge } from "@faire/retailer-visitor-shared/components/App/PopupOptInNudge/index";
import { useShouldShowMobileAppBanner } from "@faire/retailer-visitor-shared/components/App/useShouldShowMobileAppBanner";
import { useHeaderPriorityStore } from "@faire/retailer-visitor-shared/components/HeaderPriorityRenderer/HeaderPriorityPriovider";
import { useIsHeaderDropdownVisible } from "@faire/retailer-visitor-shared/components/HeaderPriorityRenderer/useIsHeaderDropdownVisible";
import { LazyAbandonedBrandCartNudge } from "@faire/retailer-visitor-shared/features/Nudges/AbandonedBrandCartNudge/LazyAbandonedBrandCartNudge";
import {
  shouldAbandonedBrandCartNudge,
  useAbandonedBrandCartNudge,
} from "@faire/retailer-visitor-shared/features/Nudges/AbandonedBrandCartNudge/useAbandonedBrandCartNudge";
import { LazyReferralAdoptionNudge } from "@faire/retailer-visitor-shared/features/Nudges/ReferralAdoptionNudges/LazyReferralAdoptionNudge";
import {
  isPathEligibleForReferralAdoptionNudges,
  useReferralAdoptionNudgeEligibility,
} from "@faire/retailer-visitor-shared/features/Nudges/ReferralAdoptionNudges/useReferralAdoptionNudgeEligibility";
import { LazyShopifyPlanUpsellNudge } from "@faire/retailer-visitor-shared/features/Nudges/ShopifyPlanUpsellNudge/index";
import { isPathEligibleForShopifyNudge } from "@faire/retailer-visitor-shared/features/Nudges/ShopifyPlanUpsellNudge/isPathEligibleForShopifyNudge";
import {
  AppNudgeProvider,
  useAppNudgeStore,
} from "@faire/retailer-visitor-shared/features/Nudges/useAppNudgeStore";
import { useInsiderTermsAndConditionsConsentStatus } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderTermsAndConditionsConsentStatus";
import { useShopifyOfferEligibility } from "@faire/retailer-visitor-shared/hooks/shopifyOnboarding/useShopifyOfferEligibility";
import { isLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { matchPathWithLocale } from "@faire/web--source/ui/routing/react-utils";
import { removeLocaleFromUrl } from "@faire/web--source/ui/routing/util";
import confirmIdentity from "@faire/web-api--source/routes/www/confirm-identity";
import expandingBusinessesOffer from "@faire/web-api--source/routes/www/expanding-businesses-offer";
import expandingBusinessesOfferApply from "@faire/web-api--source/routes/www/expanding-businesses-offer/apply";
import smallBusinessGrant from "@faire/web-api--source/routes/www/small-business-grant";
import insiderTermsOfService from "@faire/web-api--source/routes/www/tos-insider";
import once from "lodash/once";
import React, { Suspense, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";

export const AppNudges: React.FC = () => {
  const history = useHistory();
  const { isAnyHeaderDropdownVisible } = useIsHeaderDropdownVisible();
  const [{ isNotificationPreviewVisible }] = useHeaderPriorityStore([
    "isNotificationPreviewVisible",
  ]);

  const isHeaderComponentVisible =
    isAnyHeaderDropdownVisible || isNotificationPreviewVisible;

  const { userCanPlaceOrder: userCanAcceptTerms } = useUserStore([
    "userCanPlaceOrder",
  ]);

  const isInsiderTermsAndConditionsPage = matchPathWithLocale(
    history.location.pathname,
    insiderTermsOfService()
  );

  const { isInsiderTermsAndConditionsPendingOrOutdated } =
    useInsiderTermsAndConditionsConsentStatus();

  /* If the user is in the Insider terms and conditions page and their insider terms and conditions consent is pending or outdated.
   * We'll show the insider terms and conditions consent banner in the Insider terms and conditions page.
   * And we'll hide all nudges to not deviate the user from the Insider terms and conditions consent flow. */
  const shouldHideNudges =
    (isInsiderTermsAndConditionsPage &&
      isInsiderTermsAndConditionsPendingOrOutdated &&
      userCanAcceptTerms) ||
    isHeaderComponentVisible;

  const blockedPages = [
    confirmIdentity(),
    expandingBusinessesOffer(),
    expandingBusinessesOfferApply(),
    smallBusinessGrant(),
  ];

  const isBlockedPage = blockedPages.some((blockedPage: string) => {
    return matchPathWithLocale(history.location.pathname, blockedPage);
  });

  if (shouldHideNudges || isBlockedPage) {
    return null;
  }

  return (
    <AppNudgeProvider>
      <AppNudgesBase />
    </AppNudgeProvider>
  );
};

const AppNudgesBase: React.FC = () => {
  const { pathname: pathnameWithMaybeLocale } = useLocation();

  const pathname = useMemo(
    () => removeLocaleFromUrl(pathnameWithMaybeLocale),
    [pathnameWithMaybeLocale]
  );
  const { isMobile, isTabletAndAbove } = useViewport();
  const abandonedBrandCartNudge = useAbandonedBrandCartNudge();
  const shouldShowMobileAppBanner = useShouldShowMobileAppBanner();

  const [{ hasAcknowledgedKnownReferrerNudge }] = useAppNudgeStore([
    "hasAcknowledgedKnownReferrerNudge",
  ]);

  const shouldRenderAbandonedBrandCartNudge =
    abandonedBrandCartNudge.hasNudge && shouldAbandonedBrandCartNudge(pathname);

  const isMobileAppBannerShown = once(() => shouldShowMobileAppBanner);
  const shouldRenderPopupOptInNudge =
    isLoggedInRetailer() && !(isMobile && isMobileAppBannerShown());

  const { showEligibleShopifyOfferIncentives } = useShopifyOfferEligibility();

  const {
    shouldShowReferralAdoptionNudge,
    hasAcknowledgedReferralAdoptionNudge,
  } = useReferralAdoptionNudgeEligibility();

  if (
    showEligibleShopifyOfferIncentives &&
    isPathEligibleForShopifyNudge(pathname) &&
    isTabletAndAbove
  ) {
    return (
      <Suspense fallback={null}>
        <LazyShopifyPlanUpsellNudge />
      </Suspense>
    );
  }

  if (
    hasAcknowledgedKnownReferrerNudge ||
    hasAcknowledgedReferralAdoptionNudge
  ) {
    // Block nudges until next session after seeing OwF nudge
    return null;
  }

  if (
    shouldShowReferralAdoptionNudge &&
    isPathEligibleForReferralAdoptionNudges(pathname)
  ) {
    return (
      <Suspense fallback={null}>
        <LazyReferralAdoptionNudge />
      </Suspense>
    );
  }

  if (shouldRenderAbandonedBrandCartNudge) {
    return (
      <Suspense fallback={null}>
        <LazyAbandonedBrandCartNudge
          abandonedBrandCartNudge={abandonedBrandCartNudge}
        />
      </Suspense>
    );
  }

  if (shouldRenderPopupOptInNudge) {
    return (
      <Suspense fallback={null}>
        <LazyPopupOptInNudge />
      </Suspense>
    );
  }

  return null;
};
